/* eslint-disable @typescript-eslint/no-explicit-any */
import {useState, useEffect, createContext, useReducer} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import AuthService from '../services/Auth/auth.service';
import UserService from '../services/User/user.service';
import Notification from '../components/Notification/Notification';
import DashboardNavRoutes from '../routes/DashboardNavRoutes';
import {path} from '../routes/path';
import {USER_TYPE} from '../constants/UserType';
import {envRole, removeStorageList} from '../utils';
import TokenService from '../services/User/token.service';
import {checkAndRefreshTokenConditionally} from '../utils/jwt';
import {isEmpty} from 'lodash';

import {RbacPermissionCheckParams} from '../types/typeRoleBasedAccessControls';
import {
  checkIfUserIsRbacPermissible,
  rbacAccessibleOnlyRoutes,
} from '../utils/role-based-access-control/RoleBasedAccessControlUtils';
import {decryptAesGcmToken} from '../utils/jwt';

const setSession = (token: any, refreshToken: any) => {
  if (token && refreshToken) {
    sessionStorage.setItem('accessToken', token);
    sessionStorage.setItem('refreshToken', refreshToken);
  } else {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
  }
};

const INITIALIZE = 'INITIALIZE';
const SIGN_IN = 'SIGN_IN';
const SIGN_OUT = 'SIGN_OUT';
const LOCKED_LOGIN = 'LOCKED_LOGIN';

export interface AuthContextType {
  checkRbac: (
    params: Partial<RbacPermissionCheckParams> & {
      permissionString?: string | string[];
    },
    returnItem?: any
  ) => boolean;
  signIn: (email: string, password: string) => Promise<any>;
  verification2Fa: (token: string, code: string, user: object) => Promise<any>;
  signOut: () => Promise<boolean>;
  getUser: () => any;
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  getHomeUrlFromUser: (user: any) => string | null;
  awaitGetHomeUrl: () => Promise<string | null>;
  getHomeUrl: () => string | null;
  lockedLogin: (lockedLoginTime: any) => void;
  getLockedLoginTime: () => any;
  getAccessPermissions: () => any;
  getAccessRoleDepartments: () => any;
  [key: string]: any;
}

export const AuthContext = createContext<AuthContextType | null>(null);

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  lockedLoginTime: null,
  accessRoleDepartments: null,
  accessRoleDepartmentName: null,
  accessPermissions: [],
};

let notificationTimeout: any;

function AuthProvider({children}: any) {
  const authService = new AuthService();
  const userService = new UserService();
  const navigate = useNavigate();
  const location = useLocation();

  const [notification, showNotification] = useState<object | undefined>();
  const [currentPathname, setCurrentPathname] = useState<string | undefined>();

  useEffect(() => {
    if (notificationTimeout) clearTimeout(notificationTimeout);
    notificationTimeout = setTimeout(() => showNotification(undefined), 4000);
    return () => notificationTimeout && clearTimeout(notificationTimeout);
  }, [notification]);

  /* --------------------------------- Reducer -------------------------------- */
  const reducer = (state: any, action: any) => {
    if (action.type === INITIALIZE) {
      const {isAuthenticated, user} = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        user,
      };
    }
    if (action.type === SIGN_IN) {
      const {user} = action.payload;
      return {...state, isAuthenticated: true, user};
    }
    if (action.type === SIGN_OUT) {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    if (action.type === LOCKED_LOGIN) {
      const {lockedLoginTime} = action.payload;
      if (lockedLoginTime)
        window.localStorage.setItem('lockedLoginTime', lockedLoginTime);
      else window.localStorage.removeItem('lockedLoginTime');
      return {
        ...state,
        lockedLoginTime,
      };
    }
    return state;
  };

  /* --------------------------------- Actions -------------------------------- */
  const signIn = async (email: string, password: string) => {
    return await authService.signIn(email, password).then(async res => {
      const {success} = res;
      if (success === true) {
        const {
          token,
          refreshToken,
          enabled2FA,
          lockedDateTime2FA,
          isChangePassword,
        } = res.data;
        const user = {
          email,
          password,
          token,
        };
        if (!enabled2FA) {
          if (isChangePassword) {
            return navigate(
              `${path.AUTH}/${path.CHANGE_PASSWORD}?token=${token}`
            );
          }
          setSession(token, refreshToken);
          dispatch({type: SIGN_IN, payload: {user}});
        }
        return {
          success,
          enabled2FA,
          lockedDateTime2FA,
          token,
          user,
        };
      }
      return {
        success,
      };
    });
  };

  const verification2Fa = async (token: string, code: string, user: object) => {
    return await authService.verification2Fa(token, code).then(async res => {
      const {success, data, errorMessage} = res;
      const {token, refreshToken, attemptsLeft, lockdown, isChangePassword} =
        data || {};
      if (success === true) {
        if (isChangePassword) {
          return navigate(
            `${path.AUTH}/${path.CHANGE_PASSWORD}?token=${token}`
          );
        }
        setSession(token, refreshToken);
        dispatch({type: SIGN_IN, payload: {user}});
        return {
          success,
        };
      }
      return {
        success,
        errorMessage,
        attemptsLeft,
        lockdown,
      };
    });
  };

  const signOut = async () => {
    return await authService
      .signOut({Authorization: `Bearer ${getAccessToken()}`})
      .then(async data => {
        if (data.success === true) {
          setSession(null, null);
          dispatch({type: SIGN_OUT});
          return true;
        }
        return false;
      });
  };

  const getUser = () => {
    return state.user;
  };

  const getAccessToken = () => window.sessionStorage.getItem('accessToken');

  const getRefreshToken = () => window.sessionStorage.getItem('refreshToken');

  const getHomeUrlFromUser = (user: any) => {
    if (isEmpty(user)) return `/${path.AUTH}/${path.SIGN_IN}`;

    if (user?.data?.type === +envRole) {
      // If current role is Administrator
      if (user?.data?.type === USER_TYPE.ADMINISTRATOR) {
        // Get the first accessible route based on user's current RBAC permissions
        const getFirstRbacAccessibleRoute = rbacAccessibleOnlyRoutes({
          sidebarItems: DashboardNavRoutes?.[0]?.pages,
          userAccessPermissions: user?.data?.accessPermissions,
          userRoleDepartments: user?.data?.accessRoleDepartments,
        })?.suggestedRoute;

        if (!getFirstRbacAccessibleRoute) {
          console.warn('getHomeUrlFromUser: No suggested route found');
        }

        const rbacAccessibleRoute =
          getFirstRbacAccessibleRoute ?? `/${path.ISLAND_MEMBERS}`;

        // prepend / to the route
        const normalisedRoute = rbacAccessibleRoute.startsWith('/')
          ? rbacAccessibleRoute
          : `/${rbacAccessibleRoute}`;

        // Return route
        return normalisedRoute;
      }

      // Return merchant route
      return `/${path.ISLAND_PARTNERS}/${user?.data?.merchantId}`;
    }

    return null;
  };

  const awaitGetHomeUrl = async () => {
    const user = await updateUser();
    return getHomeUrlFromUser(user);
  };

  const getHomeUrl = () => {
    const user = getUser();
    return getHomeUrlFromUser(user);
  };

  const lockedLogin = (lockedLoginTime: any) => {
    dispatch({
      type: LOCKED_LOGIN,
      payload: {
        lockedLoginTime,
      },
    });
  };

  const getLockedLoginTime = () => {
    return state.lockedLoginTime;
  };

  const getAccessPermissions = () => {
    return state.user?.accessPermissions;
  };

  const getAccessRoleDepartments = () => {
    return state.user?.accessRoleDepartments;
  };

  const checkRbac = (
    {permissionString, module, resource, operation}: RbacPermissionCheckParams,
    returnItem?: any
  ) => {
    const hasPermission = checkIfUserIsRbacPermissible(
      state.user?.accessPermissions,
      state.user?.accessRoleDepartments,
      permissionString !== undefined
        ? {permissionString}
        : {module, resource, operation}
    );

    // If returnItem is provided, return the item if user has the required permission, null otherwise
    if (returnItem) {
      return hasPermission ? returnItem : null;
    }

    // If returnItem is not provided, return true or false
    return hasPermission;
  };

  /* -------------------------------- ends here ------------------------------- */
  const [state, dispatch] = useReducer(reducer, initialState);
  const [, setLoginUser] = useState<any>();
  const [, setIsDone] = useState(false);

  const updateUser = async () => {
    const onUpdateUserFailed = () => {
      setIsDone(true);
      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    };
    return await userService
      .getAll(null)
      .then(async user => {
        const {id, email, access} = user?.data;
        const decryptedAccess = await decryptAesGcmToken(access);

        if (!decryptedAccess) {
          onUpdateUserFailed();
          return null;
        }

        const {
          accessPermissions: accessesPermissionsFromDecryptedAccess,
          identifier,
          roleKey: roleKeyFromDecryptedAccess,
          roleName: roleNameFromDecryptedAccess,
        } = decryptedAccess as any;

        if (!identifier || `${id}::${email}` !== identifier) {
          onUpdateUserFailed();
          return null;
        }

        // Ensure accessPermissions and roleKey are arrays
        const accessPermissions = Array.isArray(
          accessesPermissionsFromDecryptedAccess
        )
          ? accessesPermissionsFromDecryptedAccess
          : [accessesPermissionsFromDecryptedAccess];

        const accessRoleDepartments = Array.isArray(roleKeyFromDecryptedAccess)
          ? roleKeyFromDecryptedAccess
          : [roleKeyFromDecryptedAccess];

        const accessRoleDepartmentName = roleNameFromDecryptedAccess;

        // Overwrite the existing [user] data at this point to include parsed access data
        const normalisedUserData = {
          ...user,
          data: {
            ...user?.data,
            accessPermissions,
            accessRoleDepartments,
            accessRoleDepartmentName,
          },
        };

        setIsDone(true);
        setLoginUser(normalisedUserData);
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: true,
            user: {
              ...normalisedUserData,
              email: normalisedUserData?.data?.email,
              merchantId: normalisedUserData?.data?.merchantId,
              role:
                normalisedUserData?.data?.type === USER_TYPE.ADMINISTRATOR
                  ? 'administrator'
                  : 'merchant',
              accessRoleDepartments:
                normalisedUserData?.data?.accessRoleDepartments,
              accessRoleDepartmentName:
                normalisedUserData?.data?.accessRoleDepartmentName,
              accessPermissions: normalisedUserData?.data?.accessPermissions,
            },
          },
        });

        return normalisedUserData;
      })
      .catch(() => {
        onUpdateUserFailed();
        return null;
      });
  };

  const getTokenStatus = async () => {
    let isValidCheck = true;
    let isTokenError = false;
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    await checkAndRefreshTokenConditionally({
      accessToken,
      refreshToken,
      onFailure: () => {
        isValidCheck = false;
        isTokenError = true;
        return false;
      },
      onTokenExpNotValid: async () => {
        isValidCheck = false;
        isTokenError = true;
        return false;
      },
      onTokenExpired: async () => {
        isValidCheck = false;
        isTokenError = true;
        return false;
      },
      onRefreshTokenAttemptExceed: async () => {
        isValidCheck = false;
        isTokenError = true;
        return false;
      },
      onTokenReachThreshold: () => {
        isValidCheck = false;
      },
      onRefreshTokenError: async () => {
        isValidCheck = false;
        isTokenError = true;
        return false;
      },
      onRefreshTokenSuccessully: async () => {},
      refreshTokenResponseCodeChecks: {
        [TokenService.STATUS_CODE_INTERNAL_SERVER_ERROR]: async () => {
          isValidCheck = false;
          isTokenError = true;
          removeStorageList([
            'accessToken',
            'refreshToken',
            'refreshTokenAttempt',
          ]);
          return false;
        },
      },
    });
    return {
      isValidCheck,
      isTokenError,
    };
  };

  const checkAccessTokenOnUrlChange = async (pathname: string | undefined) => {
    const excludedPaths = [
      `/${path.AUTH}/${path.SIGN_IN}`,
      `/${path.AUTH}/${path.SIGN_UP}`,
      `/${path.AUTH}/${path.FORGOT_PASSWORD}`,
      `/${path.AUTH}/${path.RESET_PASSWORD}`,
      `/${path.AUTH}/${path.CHANGE_PASSWORD}`,
    ];
    if (!pathname || excludedPaths.includes(pathname)) return;
    let isTokenError = false;
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    const forceLogout = async () => {
      await removeStorageList([
        'accessToken',
        'refreshToken',
        'refreshTokenAttempt',
      ]);
      setTimeout(() => {
        window.location.pathname = `/${path.AUTH}/${path.SIGN_IN}`;
      }, 200);
    };

    if (!accessToken || !refreshToken) {
      forceLogout();
    } else {
      const getTokenStatusResult = await getTokenStatus();
      isTokenError = getTokenStatusResult.isTokenError;

      if (isTokenError === true) {
        forceLogout();
      }
    }
  };

  const initialize = async () => {
    const onCheckLoginTokenFail = () => {
      setIsDone(true);
      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    };
    // let loginUser;
    try {
      let isValidCheck = true;
      let isTokenError = false;
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken) {
        const getTokenStatusResult = await getTokenStatus();
        isValidCheck = getTokenStatusResult.isValidCheck;
        isTokenError = getTokenStatusResult.isTokenError;
      }

      /**
       * Check user exit and validate user
       * [2022/02/09]: don't have api to validate user, so check accessToken instead, will update later
       */
      if (isValidCheck) {
        if (accessToken && refreshToken) {
          setSession(accessToken, refreshToken);
          await updateUser();
        } else {
          onCheckLoginTokenFail();
        }
      } else if (isTokenError === true) {
        onCheckLoginTokenFail();
      }
    } catch (err) {
      onCheckLoginTokenFail();
    }
    // if (isDone) {
    //   if (!loginUser && window.location.pathname !== '/auth/sign-in') {
    //     navigate('/auth/sign-in');
    //   }
    // }
    const lockedLoginTime = window.localStorage.getItem('lockedLoginTime');
    if (lockedLoginTime) {
      dispatch({
        type: LOCKED_LOGIN,
        payload: {
          lockedLoginTime:
            new Date(lockedLoginTime).getTime() > new Date().getTime()
              ? lockedLoginTime
              : null,
        },
      });
    }
  };

  useEffect(() => {
    if (currentPathname !== location.pathname) {
      setCurrentPathname(location.pathname);
    }
  }, [currentPathname, location]);

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    const tokenCheckExcludedPaths: string[] = [
      `/${path.AUTH}/${path.FA_VERITICATION}`,
      `/${path.AUTH}/${path.FORGOT_PASSWORD}`,
      `/${path.AUTH}/${path.CHANGE_PASSWORD}`,
      `/${path.AUTH}/${path.CHANGE_PASSWORD_SUCCESS}`,
      `/${path.AUTH}/${path.RESET_PASSWORD_SUCCESS}`,
      `/${path.AUTH}/${path.PASSWORD_RESET_EXPIRED}`,
    ];
    if (
      !tokenCheckExcludedPaths.includes(currentPathname as string) &&
      state.isInitialized
    ) {
      checkAccessTokenOnUrlChange(currentPathname);
    }
  }, [currentPathname, state.isInitialized]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signOut,
        showNotification,
        getUser,
        updateUser,
        verification2Fa,
        lockedLogin,
        getLockedLoginTime,
        getAccessToken,
        getHomeUrl,
        awaitGetHomeUrl,
        getAccessPermissions,
        getAccessRoleDepartments,
        checkRbac,
      }}
    >
      {notification && <Notification {...notification} />}
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
